<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { IBranch } from '@/interfaces/IBranch';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import { OrderMode } from '@/enums/OrderEnums';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useMenu } from '@/composition/use-menu';

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;

  const restaurantStore = useRestaurantStore();
  const menuHelper = useMenu();

  const isAcceptingOrders = computed(
    () => restaurantStore.selectedBranch?.allowOrders || false
  );

  const isFreeTier = computed(() => restaurantStore.effectiveTier === 0);

  const orderModeInfo = computed(() => {
    if (!restaurantStore.selectedBranch?.ordersMode) return null;

    return restaurantStore.selectedBranch.ordersMode === OrderMode.Native
      ? { icon: 'mdi-silverware', text: 'QueResto', color: '#ff6f00' }
      : { icon: 'mdi-whatsapp', text: 'WhatsApp', color: '#25d366' };
  });

  const orderTypes = computed(() => [
    {
      key: 'anonymousOrders',
      label: String(proxy.$t('dashboard.opsTypeAnonymous')),
      icon: 'mdi-incognito',
      enabled: restaurantStore.selectedBranch?.anonymousOrders || false,
    },
    {
      key: 'tableOrders',
      label: String(proxy.$t('dashboard.opsTypeTable')),
      icon: 'mdi-table-chair',
      enabled: restaurantStore.selectedBranch?.tableOrders || false,
    },
    {
      key: 'takeAway',
      label: String(proxy.$t('dashboard.opsTypeTakeAway')),
      icon: 'mdi-bag-checked',
      enabled: restaurantStore.selectedBranch?.takeAway || false,
    },
    {
      key: 'delivery',
      label: String(proxy.$t('dashboard.opsTypeDelivery')),
      icon: 'mdi-moped',
      enabled: restaurantStore.selectedBranch?.delivery || false,
    },
  ]);

  const enabledOrderTypes = computed(() =>
    orderTypes.value.filter((type) => type.enabled)
  );

  const orderStatusInfo = computed(() => {
    // Check free tier first - highest priority
    if (isFreeTier.value) {
      return {
        accepting: false,
        reason: 'freeTier',
        message: String(proxy.$t('dashboard.opsMsgFreeTier')),
      };
    }

    if (!isAcceptingOrders.value) {
      return {
        accepting: false,
        reason: 'disabled',
        message: String(proxy.$t('dashboard.opsMsgDisabled')),
      };
    }

    if (!restaurantStore.isBranchOpen) {
      return {
        accepting: false,
        reason: 'closed',
        message: String(proxy.$t('dashboard.opsMsgClosed')),
      };
    }

    return {
      accepting: true,
      reason: null,
      message: null,
    };
  });
</script>

<template>
  <v-card class="order-ops-card" elevation="0">
    <v-card-title class="card-header">
      <div class="header-content">
        <div class="header-left">
          <v-icon class="header-icon">mdi-store</v-icon>
          <span class="header-title">{{ $t('dashboard.opsStatus') }}</span>
        </div>
        <div class="header-right">
          <v-btn
            icon
            small
            :to="`/${restaurantStore.restaurant?.stringId}/settings?tab=orders`"
            class="settings-btn"
          >
            <v-icon small>mdi-cog</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card-title>

    <v-card-text class="card-content">
      <!-- Main Status -->
      <div class="main-status">
        <div
          class="status-indicator"
          :class="{ active: orderStatusInfo.accepting }"
        >
          <v-icon
            :color="orderStatusInfo.accepting ? 'success' : 'grey'"
            size="24"
          >
            {{
              orderStatusInfo.accepting
                ? 'mdi-check-circle'
                : 'mdi-close-circle'
            }}
          </v-icon>
          <span class="status-text">
            {{
              orderStatusInfo.accepting
                ? $t('dashboard.opsAccepting')
                : $t('dashboard.opsNotAccepting')
            }}
          </span>
        </div>

        <!-- Quick Toggle -->
        <!-- <v-switch
          :value="isAcceptingOrders"
          color="success"
          class="status-switch"
          hide-details
          disabled
        >
          <template v-slot:label>
            <span class="switch-label">{{
              isAcceptingOrders ? 'Activo' : 'Inactivo'
            }}</span>
          </template>
        </v-switch> -->
      </div>

      <v-divider class="my-3" />

      <!-- Order Mode -->
      <div v-if="orderStatusInfo.accepting && orderModeInfo" class="order-mode">
        <span class="section-label">{{ $t('dashboard.opsMode') }}</span>
        <v-chip
          small
          :color="orderModeInfo.color"
          text-color="white"
          class="mode-chip"
        >
          <v-icon small left>{{ orderModeInfo.icon }}</v-icon>
          {{ orderModeInfo.text }}
        </v-chip>
      </div>

      <!-- Order Types -->
      <div v-if="orderStatusInfo.accepting" class="order-types">
        <span class="section-label">{{ $t('dashboard.opsEnabledTypes') }}</span>
        <div class="types-grid">
          <div
            v-for="type in orderTypes"
            :key="type.key"
            class="type-item"
            :class="{ enabled: type.enabled }"
          >
            <v-icon
              :color="type.enabled ? 'success' : 'grey lighten-2'"
              size="20"
            >
              {{ type.icon }}
            </v-icon>
            <span class="type-label">{{ type.label }}</span>
            <v-icon
              :color="type.enabled ? 'success' : 'grey lighten-2'"
              size="16"
              class="status-icon"
            >
              {{ type.enabled ? 'mdi-check' : 'mdi-close' }}
            </v-icon>
          </div>
        </div>
      </div>

      <!-- No orders message -->
      <div v-if="!orderStatusInfo.accepting" class="no-orders-message">
        <v-icon
          :color="
            orderStatusInfo.reason === 'closed'
              ? 'orange'
              : orderStatusInfo.reason === 'freeTier'
              ? 'primary'
              : 'grey'
          "
          size="48"
          class="mb-2"
        >
          {{
            orderStatusInfo.reason === 'closed'
              ? 'mdi-clock-outline'
              : orderStatusInfo.reason === 'freeTier'
              ? 'mdi-crown-outline'
              : 'mdi-store-off'
          }}
        </v-icon>
        <p class="message-text">
          {{ orderStatusInfo.message }}
        </p>
        <v-btn
          small
          :color="orderStatusInfo.reason === 'freeTier' ? 'primary' : 'orange'"
          :to="
            orderStatusInfo.reason === 'freeTier'
              ? `/${restaurantStore.restaurant?.stringId}/settings?tab=plan`
              : `/${restaurantStore.restaurant?.stringId}/settings?tab=${
                  orderStatusInfo.reason === 'closed' ? 'info' : 'orders'
                }`
          "
        >
          {{
            orderStatusInfo.reason === 'closed'
              ? $t('dashboard.opsBtnSchedule')
              : orderStatusInfo.reason === 'freeTier'
              ? $t('dashboard.opsBtnUpgrade')
              : $t('dashboard.opsBtnConfigure')
          }}
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .order-ops-card {
    @apply tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-neutral-100;
    @apply tw-h-full;

    .card-header {
      @apply tw-pb-2 tw-pt-3;

      .header-content {
        @apply tw-flex tw-items-center tw-justify-between tw-w-full;

        .header-left {
          @apply tw-flex tw-items-center tw-gap-2;

          .header-icon {
            @apply tw-text-orange-500;
            font-size: 20px;
          }

          .header-title {
            @apply tw-text-base tw-font-semibold tw-text-neutral-800;
          }
        }

        .header-right {
          .settings-btn {
            @apply tw-opacity-60 hover:tw-opacity-100 tw-transition-opacity;
          }
        }
      }
    }

    .card-content {
      @apply tw-pt-0;
    }

    .main-status {
      @apply tw-flex tw-items-center tw-justify-between;

      .status-indicator {
        @apply tw-flex tw-items-center tw-space-x-2;

        &.active {
          .status-text {
            @apply tw-text-green-700 tw-font-medium;
          }
        }

        .status-text {
          @apply tw-text-neutral-700;
        }
      }

      .status-switch {
        @apply tw-ml-4;

        .switch-label {
          @apply tw-text-sm tw-text-neutral-600;
        }
      }
    }

    .order-mode {
      @apply tw-flex tw-items-center tw-space-x-3 tw-mb-3;

      .section-label {
        @apply tw-text-sm tw-font-medium tw-text-neutral-600;
      }

      .mode-chip {
        @apply tw-font-medium;
      }
    }

    .order-types {
      .section-label {
        @apply tw-text-sm tw-font-medium tw-text-neutral-600 tw-block tw-mb-2;
      }

      .types-grid {
        @apply tw-space-y-2;

        .type-item {
          @apply tw-flex tw-items-center tw-space-x-2 tw-p-2 tw-rounded;
          @apply tw-bg-neutral-50 tw-transition-colors;

          &.enabled {
            @apply tw-bg-green-50;

            .type-label {
              @apply tw-text-green-800 tw-font-medium;
            }
          }

          .type-label {
            @apply tw-text-sm tw-text-neutral-600 tw-flex-grow;
          }

          .status-icon {
            @apply tw-ml-auto;
          }
        }
      }
    }

    .no-orders-message {
      @apply tw-text-center tw-py-4;

      .message-text {
        @apply tw-text-neutral-600 tw-mb-3;
      }
    }
  }
</style>
